
// =========================================================
// DEBUTS CROP
// PROFILE · SHOPPING CART + FAVORITES
//
// MI CARRITO + ME GUSTA
//
// CARRITO:
// localStorage → cart
//
// FAVORITOS:
// API → /api/reviews/favorites/
//
// =========================================================

import {
  ShoppingCart,
  Trash2,
  Plus,
  Minus,
  ArrowRight,
  PackageOpen,
  CheckCircle2,
  Heart,
  HeartOff,
  ShoppingBag,
  ExternalLink,
  LoaderCircle,
} from "lucide-react";

import {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useNavigate,
} from "react-router-dom";

import "../../styles/favorites.css";

import ProfilePageLayout from "../../components/profile/ProfilePageLayout";


// =========================================================
// API
// =========================================================

const API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  (
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
      ? "http://localhost:8000/api"
      : "https://api.laesquinadelparques.com/api"
  );


// =========================================================
// STORAGE
// =========================================================

const CART_KEY = "cart";

const PLACEHOLDER_IMAGE =
  "/placeholder.png";


// =========================================================
// STORAGE HELPERS
// =========================================================

function readCart() {

  try {

    const raw =
      localStorage.getItem(
        CART_KEY
      );

    if (!raw) {

      return [];

    }

    const parsed =
      JSON.parse(
        raw
      );

    return Array.isArray(
      parsed
    )
      ? parsed
      : [];

  } catch (error) {

    console.error(
      "DEBUTS CROP · Error leyendo carrito:",
      error
    );

    return [];

  }

}


// =========================================================
// SAVE CART
// =========================================================

function saveCart(
  items
) {

  const safeItems =
    Array.isArray(
      items
    )
      ? items
      : [];

  localStorage.setItem(
    CART_KEY,
    JSON.stringify(
      safeItems
    )
  );

  window.dispatchEvent(
    new Event(
      "cartUpdated"
    )
  );

}


// =========================================================
// PRODUCT ID
// =========================================================

function getProductId(
  product
) {

  return (
    product?.product_id ??
    product?.id ??
    product?.pk
  );

}


// =========================================================
// PRODUCT IMAGE
// =========================================================

function getProductImage(
  product
) {

  return (
    product?.image ||
    product?.image_url ||
    product?.thumbnail ||
    product?.images?.[0] ||
    PLACEHOLDER_IMAGE
  );

}


// =========================================================
// PRODUCT CATEGORY
// =========================================================

function getProductCategory(
  product
) {

  return (
    product?.category_name ||
    product?.category ||
    "PREMIUM GROW SHOP"
  );

}


// =========================================================
// PRODUCT PRICE
// =========================================================

function getProductPrice(
  product
) {

  const value =
    product?.sale_price ??
    product?.price ??
    0;

  const number =
    Number(
      value
    );

  if (
    Number.isNaN(
      number
    )
  ) {

    return String(
      value
    );

  }

  return new Intl.NumberFormat(
    "es-CO",
    {
      style: "currency",
      currency: "COP",
      maximumFractionDigits: 0,
    }
  ).format(
    number
  );

}


// =========================================================
// FORMAT TOTAL
// =========================================================

function getFormattedTotal(
  value
) {

  return new Intl.NumberFormat(
    "es-CO",
    {
      style: "currency",
      currency: "COP",
      maximumFractionDigits: 0,
    }
  ).format(
    Number(
      value
    ) || 0
  );

}


// =========================================================
// NORMALIZE CART ITEM
// =========================================================

function normalizeCartItem(
  product
) {

  const id =
    getProductId(
      product
    );

  const quantity =
    Number(
      product?.quantity ??
      product?.qty ??
      1
    ) || 1;

  return {

    ...product,

    id,

    product_id:
      product?.product_id ??
      id,

    name:
      product?.name ||
      "Producto Debuts Crop",

    image:
      getProductImage(
        product
      ),

    category:
      getProductCategory(
        product
      ),

    category_name:
      getProductCategory(
        product
      ),

    price:
      Number(
        product?.price ??
        product?.sale_price ??
        0
      ),

    quantity,

    qty:
      quantity,

  };

}


// =========================================================
// GET AUTH TOKEN
// =========================================================

function getAccessToken() {

  try {

    const authRaw =
      localStorage.getItem(
        "auth"
      );

    if (!authRaw) {

      return null;

    }

    const auth =
      JSON.parse(
        authRaw
      );

    return (
      auth?.access ||
      null
    );

  } catch (error) {

    console.error(
      "DEBUTS CROP · Error obteniendo token:",
      error
    );

    return null;

  }

}


// =========================================================
// NORMALIZE FAVORITE
// =========================================================

function normalizeFavorite(
  favorite
) {

  if (!favorite) {

    return null;

  }

  const productId =
    favorite?.product_id ??
    favorite?.product?.id ??
    favorite?.product?.pk ??
    null;

  return {

    ...favorite,

    product_id:
      productId,

    name:
      favorite?.product_name ||
      favorite?.product?.name ||
      "Producto Debuts Crop",

    image:
      favorite?.product_image ||
      favorite?.product?.image ||
      favorite?.product?.image_url ||
      PLACEHOLDER_IMAGE,

    category:
      favorite?.product_category ||
      favorite?.product_category_name ||
      favorite?.product?.category_name ||
      favorite?.product?.category ||
      "PREMIUM GROW SHOP",

    price:
      Number(
        favorite?.product_price ??
        favorite?.product?.price ??
        favorite?.product?.sale_price ??
        0
      ),

  };

}


// =========================================================
// COMPONENT
// =========================================================

export default function Favorites() {

  const navigate =
    useNavigate();


  // =======================================================
  // ACTIVE TAB
  // =======================================================

  const [
    activeTab,
    setActiveTab,
  ] = useState(
    "cart"
  );


  // =======================================================
  // CART
  // =======================================================

  const [
    cart,
    setCart,
  ] = useState(
    []
  );


  // =======================================================
  // FAVORITES
  // =======================================================

  const [
    favorites,
    setFavorites,
  ] = useState(
    []
  );


  // =======================================================
  // FAVORITES LOADING
  // =======================================================

  const [
    favoritesLoading,
    setFavoritesLoading,
  ] = useState(
    false
  );


  // =======================================================
  // FAVORITES ERROR
  // =======================================================

  const [
    favoritesError,
    setFavoritesError,
  ] = useState(
    ""
  );


  // =======================================================
  // MESSAGE
  // =======================================================

  const [
    message,
    setMessage,
  ] = useState(
    ""
  );


  // =======================================================
  // LOAD CART
  // =======================================================

  const loadCart =
    () => {

      const storedCart =
        readCart();

      setCart(
        storedCart.map(
          normalizeCartItem
        )
      );

    };


  // =======================================================
  // LOAD FAVORITES
  // =======================================================

  const loadFavorites =
    async () => {

      const token =
        getAccessToken();


      if (!token) {

        setFavorites(
          []
        );

        setFavoritesLoading(
          false
        );

        return;

      }


      setFavoritesLoading(
        true
      );

      setFavoritesError(
        ""
      );


      try {

        const response =
          await fetch(
            `${API_BASE_URL}/reviews/favorites/`,
            {
              method: "GET",

              headers: {
                Authorization:
                  `Bearer ${token}`,

                Accept:
                  "application/json",
              },
            }
          );


        if (
          response.status ===
          401
        ) {

          setFavoritesError(
            "Tu sesión ha expirado."
          );

          return;

        }


        if (
          !response.ok
        ) {

          throw new Error(
            `HTTP ${response.status}`
          );

        }


        const data =
          await response.json();


        let items = [];


        if (
          Array.isArray(
            data
          )
        ) {

          items = data;

        } else if (
          Array.isArray(
            data?.results
          )
        ) {

          items =
            data.results;

        } else if (
          Array.isArray(
            data?.data
          )
        ) {

          items =
            data.data;

        }


        setFavorites(
          items
            .map(
              normalizeFavorite
            )
            .filter(
              Boolean
            )
            .filter(
              (item) =>
                item.product_id
            )
        );

      } catch (error) {

        console.error(
          "DEBUTS CROP · Error cargando favoritos:",
          error
        );

        setFavoritesError(
          "No fue posible cargar tus favoritos."
        );

      } finally {

        setFavoritesLoading(
          false
        );

      }

    };


  // =======================================================
  // INITIAL LOAD
  // =======================================================

  useEffect(() => {

    loadCart();

    loadFavorites();


    const handleCartUpdate =
      () => {

        loadCart();

      };


    const handleStorage =
      () => {

        loadCart();

      };


    window.addEventListener(
      "cartUpdated",
      handleCartUpdate
    );

    window.addEventListener(
      "storage",
      handleStorage
    );


    return () => {

      window.removeEventListener(
        "cartUpdated",
        handleCartUpdate
      );

      window.removeEventListener(
        "storage",
        handleStorage
      );

    };

  }, []);


  // =======================================================
  // CART TOTAL
  // =======================================================

  const cartTotal =
    useMemo(() => {

      return cart.reduce(
        (
          total,
          item
        ) => {

          const price =
            Number(
              item?.price ??
              item?.sale_price ??
              0
            );

          const quantity =
            Number(
              item?.quantity ??
              item?.qty ??
              1
            ) || 1;


          return (
            total +
            price *
              quantity
          );

        },
        0
      );

    }, [cart]);


  // =======================================================
  // CART COUNT
  // =======================================================

  const cartCount =
    useMemo(() => {

      return cart.reduce(
        (
          total,
          item
        ) => {

          return (
            total +
            Number(
              item?.quantity ??
              item?.qty ??
              1
            )
          );

        },
        0
      );

    }, [cart]);


  // =======================================================
  // SHOW MESSAGE
  // =======================================================

  const showMessage =
    (
      text
    ) => {

      setMessage(
        text
      );


      window.setTimeout(
        () => {

          setMessage(
            ""
          );

        },
        2500
      );

    };


  // =======================================================
  // REMOVE CART ITEM
  // =======================================================

  const removeCartItem =
    (
      productId
    ) => {

      const updated =
        cart.filter(
          (item) =>
            String(
              getProductId(
                item
              )
            ) !==
            String(
              productId
            )
        );


      setCart(
        updated
      );


      saveCart(
        updated
      );


      showMessage(
        "Producto eliminado del carrito."
      );

    };


  // =======================================================
  // CHANGE QUANTITY
  // =======================================================

  const changeCartQuantity =
    (
      productId,
      amount
    ) => {

      const updated =
        cart.map(
          (item) => {

            if (
              String(
                getProductId(
                  item
                )
              ) !==
              String(
                productId
              )
            ) {

              return item;

            }


            const current =
              Number(
                item?.quantity ??
                item?.qty ??
                1
              ) || 1;


            const stock =
              Number(
                item?.stock
              );


            const maximum =
              stock > 0
                ? stock
                : 999;


            const next =
              Math.min(
                Math.max(
                  current +
                    amount,
                  1
                ),
                maximum
              );


            return {

              ...item,

              quantity:
                next,

              qty:
                next,

            };

          }
        );


      setCart(
        updated
      );


      saveCart(
        updated
      );

    };


  // =======================================================
  // OPEN PRODUCT
  // =======================================================

  const openProduct =
    (
      product
    ) => {

      const productId =
        getProductId(
          product
        );


      if (!productId) {

        return;

      }


      navigate(
        `/products/${productId}`
      );

    };


  // =======================================================
  // CONTINUE SHOPPING
  // =======================================================

  const continueShopping =
    () => {

      navigate(
        "/products"
      );

    };


  // =======================================================
  // GO TO CART
  // =======================================================

  const goToCart =
    () => {

      navigate(
        "/cart"
      );

    };


  // =======================================================
  // ADD FAVORITE TO CART
  // =======================================================

  const addFavoriteToCart =
    (
      favorite
    ) => {

      const productId =
        favorite?.product_id;


      if (!productId) {

        showMessage(
          "No fue posible identificar el producto."
        );

        return;

      }


      const existingIndex =
        cart.findIndex(
          (item) =>
            String(
              getProductId(
                item
              )
            ) ===
            String(
              productId
            )
        );


      let updated;


      if (
        existingIndex >=
        0
      ) {

        updated =
          cart.map(
            (
              item,
              index
            ) => {

              if (
                index !==
                existingIndex
              ) {

                return item;

              }


              const quantity =
                Number(
                  item?.quantity ??
                  item?.qty ??
                  1
                ) || 1;


              return {

                ...item,

                quantity:
                  quantity + 1,

                qty:
                  quantity + 1,

              };

            }
          );

      } else {

        updated = [

          ...cart,

          {

            id:
              productId,

            product_id:
              productId,

            name:
              favorite.name,

            image:
              favorite.image,

            category:
              favorite.category,

            category_name:
              favorite.category,

            price:
              favorite.price,

            quantity:
              1,

            qty:
              1,

            stock:
              favorite?.stock,

          },

        ];

      }


      setCart(
        updated
      );


      saveCart(
        updated
      );


      showMessage(
        "Producto agregado al carrito."
      );

    };


  // =======================================================
  // REMOVE FAVORITE
  // =======================================================

  const removeFavorite =
    async (
      productId
    ) => {

      const token =
        getAccessToken();


      if (!token) {

        navigate(
          "/login"
        );

        return;

      }


      try {

        const response =
          await fetch(
            `${API_BASE_URL}/reviews/favorites/product/${productId}/`,
            {
              method: "DELETE",

              headers: {
                Authorization:
                  `Bearer ${token}`,

                Accept:
                  "application/json",
              },
            }
          );


        if (
          response.status ===
          401
        ) {

          navigate(
            "/login"
          );

          return;

        }


        if (
          !response.ok &&
          response.status !==
            404
        ) {

          throw new Error(
            `HTTP ${response.status}`
          );

        }


        setFavorites(
          (
            current
          ) =>
            current.filter(
              (
                item
              ) =>
                String(
                  item.product_id
                ) !==
                String(
                  productId
                )
            )
        );


        window.dispatchEvent(
          new Event(
            "favoritesUpdated"
          )
        );


        showMessage(
          "Producto eliminado de Me gusta."
        );

      } catch (error) {

        console.error(
          "DEBUTS CROP · Error eliminando favorito:",
          error
        );

        showMessage(
          "No fue posible eliminar el favorito."
        );

      }

    };


  // =======================================================
  // RENDER
  // =======================================================

  return (

    <ProfilePageLayout
      title="Mi carrito y Me gusta"
      subtitle="Administra tus productos guardados y revisa lo que tienes listo para comprar."
      icon={ShoppingCart}
    >


      {/* =================================================
          MESSAGE
      ================================================= */}

      {message && (

        <div className="profile-cart-message">

          <CheckCircle2
            size={18}
          />

          <span>
            {message}
          </span>

        </div>

      )}


      {/* =================================================
          TABS
      ================================================= */}

      <div className="profile-cart-tabs">

        {/* CARRITO */}

        <button
          type="button"
          className={
            activeTab === "cart"
              ? "profile-cart-tab active"
              : "profile-cart-tab"
          }
          onClick={() =>
            setActiveTab(
              "cart"
            )
          }
        >

          <ShoppingCart
            size={19}
          />

          <span>
            MI CARRITO
          </span>

          <strong>
            {cartCount}
          </strong>

        </button>


        {/* ME GUSTA */}

        <button
          type="button"
          className={
            activeTab === "favorites"
              ? "profile-cart-tab active"
              : "profile-cart-tab"
          }
          onClick={() =>
            setActiveTab(
              "favorites"
            )
          }
        >

          <Heart
            size={19}
          />

          <span>
            ME GUSTA
          </span>

          <strong>
            {favorites.length}
          </strong>

        </button>

      </div>


      {/* =================================================
          CART
      ================================================= */}

      {activeTab === "cart" && (

        <section className="profile-template-card profile-cart-card">


          {/* =================================================
              HEADER
          ================================================= */}

          <div className="profile-card-title">

            <ShoppingCart
              size={21}
            />

            <div>

              <h2>
                Mi carrito
              </h2>

              <p>

                {cartCount > 0

                  ? `${cartCount} ${
                      cartCount === 1
                        ? "producto"
                        : "productos"
                    } en tu carrito`

                  : "No tienes productos en el carrito."
                }

              </p>

            </div>

          </div>


          {/* =================================================
              EMPTY CART
          ================================================= */}

          {cart.length === 0 ? (

            <div className="profile-cart-empty">

              <div className="profile-cart-empty-icon">

                <PackageOpen
                  size={42}
                />

              </div>


              <span className="profile-cart-empty-label">
                CART EMPTY
              </span>


              <h3>
                Tu carrito está vacío
              </h3>


              <p>
                Agrega productos desde
                la tienda para comenzar
                tu compra.
              </p>


              <button
                type="button"
                className="profile-cart-continue-button"
                onClick={
                  continueShopping
                }
              >

                EXPLORAR PRODUCTOS

                <ArrowRight
                  size={17}
                />

              </button>

            </div>

          ) : (

            <>


              {/* =============================================
                  CART LIST
              ============================================= */}

              <div className="profile-cart-list">

                {cart.map(
                  (
                    item
                  ) => {

                    const productId =
                      getProductId(
                        item
                      );


                    const quantity =
                      Number(
                        item?.quantity ??
                        item?.qty ??
                        1
                      ) || 1;


                    const unitPrice =
                      Number(
                        item?.price ??
                        item?.sale_price ??
                        0
                      );


                    const subtotal =
                      unitPrice *
                      quantity;


                    return (

                      <article
                        className="profile-cart-item"
                        key={
                          productId
                        }
                      >


                        {/* IMAGE */}

                        <button
                          type="button"
                          className="profile-cart-image-button"
                          onClick={() =>
                            openProduct(
                              item
                            )
                          }
                          aria-label={`Ver ${item.name}`}
                        >

                          <img
                            src={
                              getProductImage(
                                item
                              )
                            }
                            alt={
                              item.name
                            }
                            onError={(
                              event
                            ) => {

                              event.currentTarget.src =
                                PLACEHOLDER_IMAGE;

                            }}
                          />

                        </button>


                        {/* PRODUCT INFO */}

                        <div className="profile-cart-main">

                          <span className="profile-cart-category">

                            {getProductCategory(
                              item
                            )}

                          </span>


                          <h3>
                            {item.name}
                          </h3>


                          {item.sku && (

                            <small>
                              REF. {item.sku}
                            </small>

                          )}


                          <strong>

                            {getProductPrice(
                              item
                            )}

                          </strong>

                        </div>


                        {/* QUANTITY */}

                        <div className="profile-cart-quantity">

                          <button
                            type="button"
                            onClick={() =>
                              changeCartQuantity(
                                productId,
                                -1
                              )
                            }
                            disabled={
                              quantity <=
                              1
                            }
                            aria-label="Disminuir cantidad"
                          >

                            <Minus
                              size={15}
                            />

                          </button>


                          <strong>
                            {quantity}
                          </strong>


                          <button
                            type="button"
                            onClick={() =>
                              changeCartQuantity(
                                productId,
                                1
                              )
                            }
                            aria-label="Aumentar cantidad"
                          >

                            <Plus
                              size={15}
                            />

                          </button>

                        </div>


                        {/* SUBTOTAL */}

                        <div className="profile-cart-subtotal">

                          <small>
                            SUBTOTAL
                          </small>


                          <strong>

                            {getFormattedTotal(
                              subtotal
                            )}

                          </strong>

                        </div>


                        {/* REMOVE */}

                        <button
                          type="button"
                          className="profile-cart-remove"
                          onClick={() =>
                            removeCartItem(
                              productId
                            )
                          }
                          aria-label={`Eliminar ${item.name} del carrito`}
                        >

                          <Trash2
                            size={17}
                          />

                        </button>

                      </article>

                    );

                  }
                )}

              </div>


              {/* =============================================
                  CART SUMMARY
              ============================================= */}

              <div className="profile-cart-summary">


                <div className="profile-cart-summary-row">

                  <span>
                    PRODUCTOS
                  </span>

                  <strong>
                    {cartCount}
                  </strong>

                </div>


                <div className="profile-cart-summary-row">

                  <span>
                    SUBTOTAL
                  </span>

                  <strong>

                    {getFormattedTotal(
                      cartTotal
                    )}

                  </strong>

                </div>


                <div className="profile-cart-summary-divider" />


                <div className="profile-cart-summary-total">

                  <div>

                    <span>
                      TOTAL
                    </span>

                    <small>
                      Impuestos y entrega
                      se calculan en checkout
                    </small>

                  </div>


                  <strong>

                    {getFormattedTotal(
                      cartTotal
                    )}

                  </strong>

                </div>


                {/* ACTIONS */}

                <div className="profile-cart-actions">


                  <button
                    type="button"
                    className="profile-cart-continue-button secondary"
                    onClick={
                      continueShopping
                    }
                  >

                    SEGUIR COMPRANDO

                    <ArrowRight
                      size={17}
                    />

                  </button>


                  <button
                    type="button"
                    className="profile-cart-checkout-button"
                    onClick={
                      goToCart
                    }
                  >

                    CONTINUAR AL CARRITO

                    <ArrowRight
                      size={18}
                    />

                  </button>

                </div>

              </div>

            </>

          )}

        </section>

      )}


      {/* =================================================
          FAVORITES
      ================================================= */}

      {activeTab === "favorites" && (

        <section className="profile-template-card profile-favorites-card">


          {/* =================================================
              HEADER
          ================================================= */}

          <div className="profile-card-title">

            <Heart
              size={21}
            />

            <div>

              <h2>
                Me gusta
              </h2>

              <p>

                {favorites.length > 0

                  ? `${favorites.length} ${
                      favorites.length === 1
                        ? "producto guardado"
                        : "productos guardados"
                    }`

                  : "No tienes productos guardados."
                }

              </p>

            </div>

          </div>


          {/* =================================================
              ERROR
          ================================================= */}

          {favoritesError && (

            <div className="profile-cart-message">

              <HeartOff
                size={18}
              />

              <span>
                {favoritesError}
              </span>

            </div>

          )}


          {/* =================================================
              LOADING
          ================================================= */}

          {favoritesLoading ? (

            <div className="profile-cart-empty">

              <div className="profile-cart-empty-icon">

                <LoaderCircle
                  size={42}
                  className="profile-favorites-loading"
                />

              </div>


              <span className="profile-cart-empty-label">
                LOADING
              </span>


              <h3>
                Cargando favoritos
              </h3>


              <p>
                Estamos consultando tus
                productos guardados.
              </p>

            </div>

          ) : favorites.length === 0 ? (

            /* =================================================
               EMPTY FAVORITES
            ================================================= */

            <div className="profile-cart-empty">

              <div className="profile-cart-empty-icon">

                <Heart
                  size={42}
                />

              </div>


              <span className="profile-cart-empty-label">
                NO FAVORITES
              </span>


              <h3>
                Todavía no tienes Me gusta
              </h3>


              <p>
                Guarda tus productos favoritos
                para encontrarlos rápidamente
                cuando quieras comprar.
              </p>


              <button
                type="button"
                className="profile-cart-continue-button"
                onClick={
                  continueShopping
                }
              >

                EXPLORAR PRODUCTOS

                <ArrowRight
                  size={17}
                />

              </button>

            </div>

          ) : (

            /* =================================================
               FAVORITES GRID
            ================================================= */

            <div className="profile-favorites-grid">

              {favorites.map(
                (
                  favorite
                ) => {

                  const productId =
                    favorite.product_id;


                  return (

                    <article
                      className="profile-favorite-item"
                      key={
                        favorite.id ??
                        productId
                      }
                    >


                      {/* IMAGE */}

                      <button
                        type="button"
                        className="profile-favorite-image"
                        onClick={() =>
                          openProduct(
                            favorite
                          )
                        }
                        aria-label={`Ver ${favorite.name}`}
                      >

                        <img
                          src={
                            favorite.image
                          }
                          alt={
                            favorite.name
                          }
                          onError={(
                            event
                          ) => {

                            event.currentTarget.src =
                              PLACEHOLDER_IMAGE;

                          }}
                        />


                        <span className="profile-favorite-image-overlay">

                          <ExternalLink
                            size={18}
                          />

                        </span>

                      </button>


                      {/* INFO */}

                      <div className="profile-favorite-info">

                        <span className="profile-cart-category">

                          {favorite.category}

                        </span>


                        <h3>
                          {favorite.name}
                        </h3>


                        <strong>

                          {getProductPrice(
                            favorite
                          )}

                        </strong>

                      </div>


                      {/* ACTIONS */}

                      <div className="profile-favorite-actions">


                        {/* ADD TO CART */}

                        <button
                          type="button"
                          className="profile-favorite-cart-button"
                          onClick={() =>
                            addFavoriteToCart(
                              favorite
                            )
                          }
                        >

                          <ShoppingBag
                            size={16}
                          />

                          AGREGAR AL CARRITO

                        </button>


                        {/* REMOVE */}

                        <button
                          type="button"
                          className="profile-favorite-remove-button"
                          onClick={() =>
                            removeFavorite(
                              productId
                            )
                          }
                          aria-label="Quitar de Me gusta"
                          title="Quitar de Me gusta"
                        >

                          <HeartOff
                            size={17}
                          />

                        </button>

                      </div>

                    </article>

                  );

                }
              )}

            </div>

          )}

        </section>

      )}

    </ProfilePageLayout>

  );

}
